// Frontend mirror of the backend unit-conversion helpers
// (/backend/recipes/shared/units.ts). Operates on the units list returned by
// useGetUnits. Same-type conversions always work; weight↔volume conversions
// require a per-ingredient density (grams per ml).

export interface Unit {
  id: string
  name: string
  unit_type: string
  to_base: number
  sort_order?: number
}

export function unitMap(units: Unit[]): Map<string, Unit> {
  const map = new Map<string, Unit>()
  for (const u of units) map.set((u.name ?? '').trim().toLowerCase(), u)
  return map
}

function lookup(map: Map<string, Unit>, name: string | null | undefined): Unit | null {
  if (!name) return null
  return map.get(name.trim().toLowerCase()) ?? null
}

/** Density in grams per ml from a weight amount per a volume amount. */
export function densityGramsPerMl(
  weightQty: number | null | undefined,
  weightUnit: string | null | undefined,
  volumeQty: number | null | undefined,
  volumeUnit: string | null | undefined,
  map: Map<string, Unit>,
): number | null {
  if (weightQty == null || volumeQty == null) return null
  const w = lookup(map, weightUnit)
  const v = lookup(map, volumeUnit)
  if (!w || !v || w.unit_type !== 'weight' || v.unit_type !== 'volume') return null
  const grams = weightQty * w.to_base
  const ml = volumeQty * v.to_base
  if (ml <= 0) return null
  return grams / ml
}

/** Converts `qty` from `fromUnit` to `toUnit`, or null if not convertible. */
export function convertQuantity(
  qty: number,
  fromUnit: string | null | undefined,
  toUnit: string | null | undefined,
  map: Map<string, Unit>,
  densityGPerMl: number | null = null,
): number | null {
  const from = lookup(map, fromUnit)
  const to = lookup(map, toUnit)
  if (!from || !to) return null
  if (from.unit_type === to.unit_type) return (qty * from.to_base) / to.to_base

  if (densityGPerMl == null || densityGPerMl <= 0) return null
  const fromBase = qty * from.to_base
  let toBaseAmount: number
  if (from.unit_type === 'volume' && to.unit_type === 'weight') {
    toBaseAmount = fromBase * densityGPerMl
  } else if (from.unit_type === 'weight' && to.unit_type === 'volume') {
    toBaseAmount = fromBase / densityGPerMl
  } else {
    return null
  }
  return toBaseAmount / to.to_base
}

/** Comparable magnitude (grams) for FDA ordering. */
export function predominance(
  qty: number | null | undefined,
  unit: string | null | undefined,
  map: Map<string, Unit>,
  densityGPerMl: number | null = null,
): number {
  if (qty == null || Number.isNaN(qty)) return -Infinity
  const u = lookup(map, unit)
  if (!u) return qty
  if (u.unit_type === 'weight') return qty * u.to_base
  if (u.unit_type === 'volume') {
    const ml = qty * u.to_base
    return densityGPerMl != null && densityGPerMl > 0 ? ml * densityGPerMl : ml
  }
  return qty
}
